import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, put, deleteFn } from '@/lib/api';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useForm, FormProvider } from 'react-hook-form';
import { Packet, PacketStatus, PacketVisibility, APIError, PacketWithCandidate, RoleType, User, Ratification, getInitials, isPacketSupportingContentEditable } from '@promo/shared';
import { packetStatusLabels } from '@/lib/packetStatus';
import { UserAvatar } from '@/components/ui/user-avatar';
import { CriteriaSection } from '../components/CriteriaSection';
import { EndorsementsSection } from '../components/EndorsementsSection';
import { ConcernsSection } from '../components/ConcernsSection';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { CreatePacketFormFields } from '../components/CreatePacketForm';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Spinner } from '@/components/ui/spinner';
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { EditablePacketSection } from '../components/EditablePacketSection';
import type { PacketUpdatePayload } from '../components/EditablePacketSection';
import { StatusSlider } from '@/components/ui/status-slider';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { ChevronDown, FileDown } from 'lucide-react';
import { BlindAssessmentsSection } from '../components/BlindAssessmentsSection';
import { UnblindedAssessmentsSection } from '../components/UnblindedAssessmentsSection';
import { useInvitations } from '../hooks/useInvitations';
import { RatificationForm } from '../components/RatificationForm';
import { StatusUpdateButtons } from '../components/StatusUpdateButtons';
import { useBlindAssessmentProgress } from '../hooks/useBlindAssessmentProgress';
import { 
  canViewCompletedPacketDetails,
  determineShowAssessmentTabs 
} from '../utils/packetVisibility';
import { WithdrawnPacketAlert } from '../components/WithdrawnPacketAlert';
import { useGlobalRoles } from '@/hooks/useGlobalRoles';
import { AttemptBadge } from '@/components/ui/attempt-badge';
import { PastAttemptsList } from '../components/PastAttemptsList';
import { BarRaiserReviewSection } from '../components/BarRaiserReviewSection';
import { parseCoManagerEmails } from '../components/CoManagersField';
import { PacketVisibilityControl } from '../components/PacketVisibilityControl';
import { exportPacketToPdf } from '../utils/packetPdfExport';

// Helper to create schema with manager info in closure
function getCreatePacketSchema(managerEmail: string, managerId: string) {
  return z.object({
    candidateEmail: z.string()
      .email('Invalid email address')
      .regex(/@tebra\.com$/, 'Must be a tebra.com email address'),
    seniorLeaderId: z.string().min(1, 'Senior leader is required'),
    currentTitle: z.string().optional(),
    targetTitle: z.string().optional(),
    timeInRole: z.string().optional(),
    lastPromotedDate: z.date().nullable().optional(),
    year: z.string(),
    coManagerEmails: z.string().optional(),
  }).superRefine((data, ctx) => {
    const candidateEmail = data.candidateEmail.trim().toLowerCase();
    const coManagerEmails = parseCoManagerEmails(data.coManagerEmails || '');
    // Compare candidate email to manager email (case-insensitive)
    if (managerEmail && candidateEmail === managerEmail.trim().toLowerCase()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Candidate, manager, and senior leader must all be different users',
        path: ['candidateEmail'],
      });
    }
    // Compare managerId to seniorLeaderId
    if (managerId && managerId === data.seniorLeaderId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Candidate, manager, and senior leader must all be different users',
        path: ['seniorLeaderId'],
      });
    }
    const invalidCoManagerEmails = coManagerEmails.filter(email => !/^[a-zA-Z0-9._%+-]+@tebra\.com$/.test(email));
    if (invalidCoManagerEmails.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Invalid @tebra.com email: ${invalidCoManagerEmails.join(', ')}`,
        path: ['coManagerEmails'],
      });
    }
    const duplicateCoManagerEmails = coManagerEmails.filter((email, index) => coManagerEmails.indexOf(email) !== index);
    if (duplicateCoManagerEmails.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Duplicate co-manager email: ${Array.from(new Set(duplicateCoManagerEmails)).join(', ')}`,
        path: ['coManagerEmails'],
      });
    }
    const reservedEmails = [candidateEmail, managerEmail.trim().toLowerCase()].filter(Boolean);
    const conflictingCoManagerEmails = coManagerEmails.filter(email => reservedEmails.includes(email));
    if (conflictingCoManagerEmails.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Already assigned to another packet role: ${conflictingCoManagerEmails.join(', ')}`,
        path: ['coManagerEmails'],
      });
    }
  });
}

// Define interface for packet creation form
type CreatePacketForm = z.infer<ReturnType<typeof getCreatePacketSchema>>;

function getPacketManagerIds(packet: PacketWithCandidate | null | undefined): string[] {
  if (!packet) return [];
  return packet.managerIds?.length ? packet.managerIds : [packet.managerId];
}

export function PacketPage() {
  // Hooks at the top level
  const { packetId } = useParams<{ packetId: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Track if user has manually selected a tab to prevent automatic resets
  const userHasSelectedTab = useRef(false);

  const isCreationMode = !packetId || packetId === 'new';
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formValues, setFormValues] = useState<CreatePacketForm | null>(null);
  const [isEditableSectionFocused, setIsEditableSectionFocused] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // Dropdown trigger ref & width measurement
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = useState(0);
  React.useLayoutEffect(() => {
    if (triggerRef.current) setTriggerWidth(triggerRef.current.offsetWidth);
  }, []);
  
  // Only fetch data if not in creation mode
  const pid = isCreationMode ? '' : packetId;
  
  const packetQuery = useQuery<PacketWithCandidate, Error>({
    queryKey: ['packet', pid],
    queryFn: () => get<PacketWithCandidate>(`/packets/${pid}`),
    enabled: !!pid && !isCreationMode,
  });
  
  // Mutation to create a new packet with enhanced error handling
  const createPacketMutation = useMutation<Packet, Error, CreatePacketForm>({
    mutationFn: async (formData) => {
      try {
        const { coManagerEmails, ...packetFormData } = formData;
        const parsedCoManagerEmails = parseCoManagerEmails(coManagerEmails || '');
        // Use the current user's ID as managerId
        const response = await post<Packet>('/packets', {
          ...packetFormData,
          managerId: auth.session.user?.id,
          assessmentYear: formData.year,
          ...(parsedCoManagerEmails.length > 0 ? { coManagerEmails: parsedCoManagerEmails } : {}),
        });
        return response;
      } catch (error) {
        console.error('Error creating packet:', error);
        
        // Handle API errors specifically
        if (error instanceof APIError) {
          throw new Error(`API Error: ${error.message}`);
        }
        
        throw error;
      }
    },
    onSuccess: (packet) => {
      queryClient.invalidateQueries({ queryKey: ['packets'] });
      queryClient.invalidateQueries({ queryKey: ['packet', packet.id] });
      
      // Add newly created packet to the cache
      queryClient.setQueryData(['packet', packet.id], packet);
      
      toast({
        title: "Success",
        description: "Packet created successfully",
      });
      
      // Navigate to the newly created packet
      navigate(`/packets/${packet.id}`);
    },
    onError: (error) => {
      toast({
        title: "Error Creating Packet",
        description: error.message || 'An unexpected error occurred',
        variant: "destructive",
      });
      
      // Close the confirmation dialog if it's open
      setShowConfirmation(false);
    },
  });

  // Mutation to update an existing packet
  const updatePacketMutation = useMutation<Packet, Error, PacketUpdatePayload>({
    mutationFn: (updateData) => {
      return put<Packet>(`/packets/${pid}`, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packet', pid] });
      toast({
        title: "Success",
        description: "Packet updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update packet: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const updateVisibilityMutation = useMutation<Packet, Error, PacketVisibility>({
    mutationFn: (visibility) => {
      return put<Packet>(`/packets/${pid}`, { visibility });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packet', pid] });
      queryClient.invalidateQueries({ queryKey: ['packets'] });
      toast({
        title: "Visibility updated",
        description: "Packet visibility was updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update packet visibility: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Mutation to delete a packet
  const deletePacketMutation = useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteFn<void>(`/packets/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packets'] });
      toast({
        title: 'Packet deleted',
        description: 'The packet was successfully deleted.',
      });
      navigate('/');
    },
    onError: (error) => {
      toast({
        title: 'Error deleting packet',
        description: `Could not delete packet: ${error.message}`,
        variant: 'destructive',
      });
    }
  });

  // Add ratification mutation and handler
  const saveRatificationMutation = useMutation<Packet, Error, { ratification: Ratification; overruleJustification?: string | null }>({
    mutationFn: (payload) => {
      return put(`/packets/${packet?.id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packet', packet?.id] });
      queryClient.invalidateQueries({ queryKey: ['packets'] });
      toast({
        title: "Success",
        description: "Ratification decision saved.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error Saving Ratification",
        description: error.message || 'Could not save ratification decision.',
        variant: "destructive",
      });
    },
    onSettled: () => {
    }
  });
  const handleSaveRatification = (data: { ratification: Ratification; justification?: string }) => {
    if (packet) {
      const payload: { ratification: Ratification; overruleJustification?: string | null } = {
        ratification: data.ratification,
        overruleJustification: null, // Default to null, will be cleared if not OVERRULED
      };

      if (data.ratification === Ratification.OVERRULED) {
        payload.overruleJustification = data.justification; // Set justification if OVERRULED
      } 
      // If data.ratification is RATIFIED, overruleJustification remains null, effectively clearing it.

      saveRatificationMutation.mutate(payload);
    }
  };

  // Setup forms with validation
  const viewingForm = useForm<Partial<Packet>>({ 
    defaultValues: packetQuery.data ?? {} as Packet 
  });
  
  const creationForm = useForm<CreatePacketForm>({
    resolver: zodResolver(getCreatePacketSchema(
      auth.session.user?.email || '',
      auth.session.user?.id || ''
    )),
    defaultValues: {
      candidateEmail: '',
      seniorLeaderId: '',
      currentTitle: '',
      targetTitle: '',
      timeInRole: '',
      lastPromotedDate: null,
      year: new Date().getFullYear().toString(),
      coManagerEmails: '',
    }
  });

  // Update form when data is loaded (for viewing mode)
  React.useEffect(() => {
    if (!isCreationMode && packetQuery.data) {
      viewingForm.reset(packetQuery.data);
    }
  }, [packetQuery.data, viewingForm, isCreationMode]);

  // Determine loading state, errors, current packet, permissions
  const isLoading = !isCreationMode && packetQuery.isLoading;
  const error = !isCreationMode ? packetQuery.error : undefined;
  const packet = isCreationMode ? null : packetQuery.data;
  const packetManagerIds = getPacketManagerIds(packet);
  const currentUserId = auth.session.user?.id ?? '';
  const isManager = !isCreationMode && !!currentUserId && packetManagerIds.includes(currentUserId);
  const isEditor = !isCreationMode && !!packet && !!currentUserId && (
    packetManagerIds.includes(currentUserId) ||
    currentUserId === packet.seniorLeaderId
  );

  const determineCurrentUserRoleForPacket = (
    currentPacket: PacketWithCandidate | null | undefined, 
    currentUser: User | null | undefined
  ): RoleType | '' => {
    if (!currentPacket || !currentUser) return ''; 

    if (getPacketManagerIds(currentPacket).includes(currentUser.id)) return RoleType.MANAGER;
    if (currentUser.id === currentPacket.seniorLeaderId) return RoleType.SENIOR_LEADER;
    
    // Placeholder for HR role detection - replace with actual logic
    // For example, if the User type from auth.session.user might have a roles array:
    // if (currentUser.roles?.includes(RoleType.HR)) return RoleType.HR;
    // Currently, this function does not specifically identify HR from a generic User object.
    // const hypotheticalUserWithRoles = currentUser as User & { roles?: RoleType[] }; 
    // if (hypotheticalUserWithRoles.roles?.includes(RoleType.HR)) return RoleType.HR;

    if (currentUser.id === currentPacket.candidateId) return RoleType.CANDIDATE;
    // Potentially, if the user has a roles array and one of them is HR:
    // if (currentUser.roles?.includes(RoleType.HR)) return RoleType.HR;
    // For now, if not manager, SL, or candidate, and no explicit HR role check is implemented from user object, return empty.
    return ''; 
  };

  const currentUserRoleForPacket: RoleType | '' = determineCurrentUserRoleForPacket(packet, auth.session.user);

  // Determine if user is an endorser
  const { data: endorserInvitations = [] } = useInvitations(
    packet?.id ?? '',
    RoleType.ENDORSER,
    { enabled: !!packet && !isCreationMode }
  );
  const isEndorser = endorserInvitations.some(inv => inv.inviteeEmail === auth.session.user?.email);

  // Hide blind and unblinded assessment tabs for candidate or endorser when status >= blind assessment but not completed
  const hideAssessmentTabs =
    (currentUserRoleForPacket === RoleType.CANDIDATE || isEndorser) &&
    packet != null &&
    ![PacketStatus.DRAFT, PacketStatus.COMPLETED].includes(packet.status);

  const handleCreatePacket = (data: CreatePacketForm) => {
    // Store form values for confirmation dialog
    setFormValues(data);
    // Show confirmation dialog
    setShowConfirmation(true);
  };

  const confirmCreatePacket = () => {
    if (formValues) {
      createPacketMutation.mutate(formValues);
    }
    // Close the dialog
    setShowConfirmation(false);
  };

  const handleUpdatePacket = (data: PacketUpdatePayload) => {
    return updatePacketMutation.mutateAsync(data);
  };

  const handleVisibilityChange = (visibility: PacketVisibility) => {
    return updateVisibilityMutation.mutateAsync(visibility);
  };

  // Deep-link logic: allow ?tab= or #anchor to select initial tab
  type TabKey = 'criteria' | 'endorsements' | 'opportunities' | 'blinded' | 'unblinded' | 'bar-raiser';
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const urlTab = (params.get('tab') as TabKey | null) ?? null;
  const hash = location.hash.slice(1);
  const hashTab = ['criteria','endorsements','opportunities','blinded','unblinded'].includes(hash)
    ? (hash as TabKey)
    : null;
  
  const [activeTab, setActiveTab] = useState<TabKey>(urlTab ?? hashTab ?? 'criteria');

  // Reset user intent flag when URL/hash parameters are present (deep-linking)
  useEffect(() => {
    if (urlTab || hashTab) {
      userHasSelectedTab.current = false;
    }
  }, [urlTab, hashTab]);

  // Reset user intent flag when navigating to a different packet
  useEffect(() => {
    userHasSelectedTab.current = false;
  }, [packetId]);

  // Sync to packet.status only when no deep-link is provided and user hasn't manually selected a tab
  useEffect(() => {
    if (!isCreationMode && packet && !urlTab && !hashTab && !userHasSelectedTab.current) {
      let defaultTab: TabKey = 'criteria';
      switch (packet.status) {
        case PacketStatus.BLINDED_ASSESSMENT:
          defaultTab = 'blinded';
          break;
        case PacketStatus.UNBLINDED_ASSESSMENT:
          defaultTab = 'unblinded';
          break;
      }
      setActiveTab(defaultTab);
    }
  }, [isCreationMode, packet, urlTab, hashTab]);

  // Prepare base tab options; opportunities will be added later once permissions are known
  const baseTabOptions = [
    { value: 'criteria', label: 'Criteria' },
    { value: 'endorsements', label: 'Endorsements' },
  ];

  // Opportunities tab conditional append (computed later but before render)
  // We'll splice later after showOpportunitiesTab variable is defined

  // Define progress steps using display labels as keys
  const sliderSteps = [
    { key: 'Draft', label: 'Draft' },
    { key: 'Assessments', label: 'Assessments' },
    { key: 'Review', label: 'Review' },
    { key: 'Decision', label: 'Decision' },
  ];

  // Map the actual packet status to the display label for the slider
  const currentStepKey = packet ? packetStatusLabels[packet.status] : 'Draft';

  // --- Fetch role assignments for the packet ---
  // TODO: Replace with packet-specific role API if available (e.g. /users?role=...&packetId=...)
  const { data: managers = [] } = useQuery<User[], Error>({
    queryKey: ['users', 'by-role', RoleType.MANAGER, packet?.id],
    queryFn: () => get<User[]>('/users', { role: RoleType.MANAGER }),
    enabled: !!packet?.id,
  });
  const { data: assessors = [] } = useQuery<User[], Error>({
    queryKey: ['users', 'by-role', RoleType.ASSESSOR, packet?.id],
    queryFn: () => get<User[]>('/users', { role: RoleType.ASSESSOR }),
    enabled: !!packet?.id,
  });
  const { data: seniorLeaders = [] } = useQuery<User[], Error>({
    queryKey: ['users', 'by-role', RoleType.SENIOR_LEADER, packet?.id],
    queryFn: () => get<User[]>('/users', { role: RoleType.SENIOR_LEADER }),
    enabled: !!packet?.id,
  });
  const { data: hrMembers = [] } = useQuery<User[], Error>({
    queryKey: ['users', 'by-role', RoleType.HR, packet?.id],
    queryFn: () => get<User[]>('/users', { role: RoleType.HR }),
    enabled: !!packet?.id,
  });
  const { data: barRaisers = [] } = useQuery<User[], Error>({
    queryKey: ['users', 'by-role', RoleType.BAR_RAISER, packet?.id],
    queryFn: () => get<User[]>('/users', { role: RoleType.BAR_RAISER }),
    enabled: !!packet?.id,
  });

  // Fetch opportunity authors (users granted OPPORTUNITY_AUTHOR role for this packet)
  const { data: opportunityAuthors = [] } = useQuery<User[], Error>({
    queryKey: ['users', 'by-role', RoleType.OPPORTUNITY_AUTHOR, packet?.id],
    queryFn: () => get<User[]>('/users', { role: RoleType.OPPORTUNITY_AUTHOR }),
    enabled: !!packet?.id,
  });

  // Build roleAssignments object (user IDs only)
  const roleAssignments: Partial<Record<RoleType, string[]>> = {
    [RoleType.MANAGER]: packet ? packetManagerIds : managers.map(u => u.id),
    [RoleType.ASSESSOR]: assessors.map(u => u.id),
    [RoleType.SENIOR_LEADER]: seniorLeaders.map(u => u.id),
    [RoleType.HR]: hrMembers.map(u => u.id),
    [RoleType.OPPORTUNITY_AUTHOR]: opportunityAuthors.map(u => u.id),
    [RoleType.BAR_RAISER]: barRaisers.map(u => u.id),
  };

  // --- Fetch global roles for the user (HR etc.) ---
  const { data: globalRoles = [] } = useGlobalRoles(auth.session.user?.id);

  // --- Determine if user is candidate or assessor for this packet ---
  const isCandidate = currentUserId === packet?.candidateId;
  const { data: assessorInvitationsForTabs = [] } = useInvitations(
    packet?.id ?? '',
    RoleType.ASSESSOR,
    { enabled: !!packet && !isCreationMode }
  );

  const isInvitedAssessor = assessorInvitationsForTabs.some(inv => inv.inviteeEmail === auth.session.user?.email);

  const isAssessor = isInvitedAssessor || !!roleAssignments[RoleType.ASSESSOR]?.includes(currentUserId);
  const isOpportunityAuthor = !!(roleAssignments as Record<string, string[]>)[RoleType.OPPORTUNITY_AUTHOR]?.includes(currentUserId);
  const isBarRaiser = !!roleAssignments[RoleType.BAR_RAISER]?.includes(currentUserId);

  // --- Determine if user can see assessment tabs ---
  const showAssessmentTabs = determineShowAssessmentTabs({
    packet: packet || null,
    userId: currentUserId,
    globalRoles,
    roleAssignments,
    isAssessor
  });

  // Blind assessment progress for status update buttons
  const { invitedCount, completedBlindCount } = useBlindAssessmentProgress(packet?.id ?? '', showAssessmentTabs);

  // --- Determine if user can see summary, badge, ratification for COMPLETED ---
  let canViewCompletedDetails = false;
  if (packet?.status === PacketStatus.COMPLETED) {
    canViewCompletedDetails = canViewCompletedPacketDetails({
      userId: currentUserId,
      candidateId: packet.candidateId,
      assessorIds: roleAssignments[RoleType.ASSESSOR] ?? [],
      globalRoles,
      packetRoles: roleAssignments,
    });
  }

  // Packet's assigned senior leader (manager assigned)
  const isPacketSeniorLeader = packet?.seniorLeaderId === auth.session.user?.id;
  // Senior leader role in general list (global)
  const isSeniorLeader = roleAssignments[RoleType.SENIOR_LEADER]?.includes(currentUserId);
  const isGlobalSeniorLeader = globalRoles.includes(RoleType.SENIOR_LEADER);
  const isHR = globalRoles.includes(RoleType.HR);
  const canManageVisibility = !!packet && !!currentUserId && (
    packetManagerIds.includes(currentUserId) ||
    currentUserId === packet.seniorLeaderId ||
    isHR
  );
  const showVisibilityControl =
    !!packet && (canManageVisibility || packet.visibility === PacketVisibility.RESTRICTED);
  const canInviteCoManagers = !!packet && !!currentUserId && (
    packetManagerIds.includes(currentUserId) ||
    currentUserId === packet.seniorLeaderId ||
    isGlobalSeniorLeader ||
    isHR
  );

  const showOpportunitiesTab = isManager || isSeniorLeader || isGlobalSeniorLeader || isHR || isAssessor || isOpportunityAuthor;
  const showBarRaiserTab = isBarRaiser || isManager || isSeniorLeader || isGlobalSeniorLeader || isHR;
  const supportingContentReadOnly = packet
    ? !isPacketSupportingContentEditable(packet.status)
    : true;

  const handleExportPdf = async () => {
    if (!packet) return;

    setIsExportingPdf(true);
    try {
      await exportPacketToPdf(packet.id);
      toast({
        title: 'PDF export ready',
        description: 'Your packet PDF download has started.',
      });
    } catch (error) {
      toast({
        title: 'Error Exporting PDF',
        description: error instanceof Error ? error.message : 'Could not export the packet.',
        variant: 'destructive',
      });
    } finally {
      setIsExportingPdf(false);
    }
  };

  // Add opportunities tab if allowed
  if (showOpportunitiesTab) {
    baseTabOptions.push({ value: 'opportunities', label: 'Opportunities' });
  }

  // Append assessment tabs when not hidden
  if (!hideAssessmentTabs) {
    baseTabOptions.push({ value: 'blinded', label: 'Blinded Assessments' });
    baseTabOptions.push({ value: 'unblinded', label: 'Unblinded Assessments' });
    if (showBarRaiserTab) {
      baseTabOptions.push({ value: 'bar-raiser', label: 'Bar Raiser Review' });
    }
  }

  // Final tab options considering hideAssessmentTabs for dropdown etc.
  const tabOptions = baseTabOptions;

  // Normalize raw tab-change value into our TabKey union and track user intent
  const handleTabChange = (value: string) => {
    userHasSelectedTab.current = true;
    setActiveTab(value as TabKey);
  };

  // If active tab is hidden (blinded/unblinded) for this user, reset to a visible tab
  useEffect(() => {
    if (hideAssessmentTabs && (activeTab === 'blinded' || activeTab === 'unblinded')) {
      setActiveTab('criteria');
    }
  }, [hideAssessmentTabs, activeTab]);

  // Render based on mode
  if (isCreationMode) {
    return (
      <FormProvider {...creationForm}>
        <div className="container mx-auto p-4 md:p-8">
          <h1 className="text-2xl font-bold mb-16">New Promotion Packet</h1>
          
          <form onSubmit={creationForm.handleSubmit(handleCreatePacket)}>
            <CreatePacketFormFields 
              isUpdating={createPacketMutation.isPending}
              className="mb-8" 
            />
            
            <div className="flex justify-end gap-4 mt-8">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => navigate('/')}
                disabled={createPacketMutation.isPending}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={createPacketMutation.isPending}
              >
                {createPacketMutation.isPending ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" />
                    Creating...
                  </>
                ) : 'Save'}
              </Button>
            </div>
          </form>
        </div>

        {/* Confirmation Dialog */}
        <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Create new promotion packet?</AlertDialogTitle>
              <AlertDialogDescription>
                You're about to create a promotion packet for {formValues?.candidateEmail}. 
                This will notify the candidate and senior leader.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={createPacketMutation.isPending}>Cancel</AlertDialogCancel>
              <Button 
                onClick={confirmCreatePacket} 
                disabled={createPacketMutation.isPending}
              >
                {createPacketMutation.isPending ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" />
                    Creating...
                  </>
                ) : 'Confirm'}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </FormProvider>
    );
  }

  return (
    <>
      <FormProvider {...viewingForm}>
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Spinner className="h-8 w-8" />
            <span className="ml-2">Loading packet...</span>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-800 p-4 rounded-md">
            <h3 className="text-lg font-semibold">Error loading data</h3>
            <p>{error?.message}</p>
          </div>
        ) : !packet ? (
          <div className="bg-yellow-50 text-yellow-800 p-4 rounded-md">
            <h3 className="text-lg font-semibold">Packet not found</h3>
            <p>The requested promotion packet could not be found.</p>
            <Button className="mt-4" onClick={() => navigate('/')}>
              Return to Dashboard
            </Button>
          </div>
        ) : (
          <div className="container mx-auto p-4 md:p-8">
            <div className="mb-16 flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
              <div className="flex items-center gap-4">
                <UserAvatar initials={getInitials(packet?.candidateName)} data-testid="candidate-avatar" />
                <h1 className="text-2xl font-bold flex items-center gap-3">
                  {packet?.candidateName}
                  {packet?.attempt && packet.attempt > 1 && (
                    <AttemptBadge attempt={packet.attempt} />
                  )}
                </h1>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                {showAssessmentTabs && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleExportPdf}
                    disabled={isExportingPdf}
                  >
                    {isExportingPdf ? (
                      <Spinner className="h-4 w-4" />
                    ) : (
                      <FileDown className="h-4 w-4" />
                    )}
                    Export to PDF
                  </Button>
                )}
                {showVisibilityControl && (
                  <PacketVisibilityControl
                    packet={packet}
                    canManage={canManageVisibility}
                    isUpdating={updateVisibilityMutation.isPending}
                    onVisibilityChange={handleVisibilityChange}
                  />
                )}
              </div>
            </div>
            
            {/* Past Attempts List */}
            {packet.attempt && packet.attempt > 1 && (
              <div className="mb-16">
                <PastAttemptsList
                  candidateId={packet.candidateId}
                  year={packet.year}
                  currentPacketId={packet.id}
                  currentAttempt={packet.attempt}
                />
              </div>
            )}
            
            {/* Withdrawn Packet Alert - shows above status when packet is withdrawn */}
            {packet.status === PacketStatus.WITHDRAWN && (
              <WithdrawnPacketAlert 
                withdrawalJustification={packet.withdrawalJustification}
                className="mb-8"
              />
            )}
            
            {/* Packet status slider: readonly */}
            <div className="container mx-auto mb-20">
              <StatusSlider
                steps={sliderSteps}
                currentStepKey={currentStepKey}
                disabled={true}
              />
            </div>
            <div className="mb-16">
              <EditablePacketSection
                packet={packet}
                onUpdate={handleUpdatePacket}
                isUpdating={updatePacketMutation.isPending || saveRatificationMutation.isPending}
                readOnly={packet.status !== PacketStatus.DRAFT || !isEditor}
                onDelete={isEditor && packet.status === PacketStatus.DRAFT ? () => deletePacketMutation.mutate(packet.id) : undefined}
                currentUserRole={currentUserRoleForPacket}
                onFocusChange={setIsEditableSectionFocused}
                canViewCompletedDetails={canViewCompletedDetails}
                canInviteCoManagers={canInviteCoManagers}
              />
            </div>

            {/* Conditionally render StatusUpdateButtons - hide when withdrawn */} 
            {!isEditableSectionFocused && packet && packet.status !== PacketStatus.WITHDRAWN && (
              <div className="mt-10 mb-16 flex justify-end">
                <StatusUpdateButtons
                  packet={packet}
                  currentUserRole={currentUserRoleForPacket}
                  currentUserID={currentUserId}
                  managerIds={packetManagerIds}
                  invitedAssessorCount={invitedCount}
                  completedBlindAssessmentCount={completedBlindCount}
                />
              </div>
            )}

            {/* Ratification Form Logic */}
            {(() => {
              if (isCreationMode || !packet) {
                return null;
              }
              const isSLReviewStatus = packet.status === PacketStatus.SENIOR_LEADER_REVIEW || packet.status === PacketStatus.HR_REVIEW;
              const isRatificationPresent = packet.ratification != null;
              // Condition for Senior Leader to make/edit ratification
              const canSLEditRatification =
                currentUserId === packet.seniorLeaderId && isSLReviewStatus;
              const canSLViewRatification =
                currentUserId === packet.seniorLeaderId && isRatificationPresent;
              // Condition for Manager to view an existing ratification
              const canManagerViewRatification =
                isManager && isRatificationPresent;
              // Condition for HR to view an existing ratification
              const canHRViewRatification =
                currentUserRoleForPacket === RoleType.HR && isRatificationPresent;
              const shouldRenderRatificationForm = 
                canSLEditRatification || canSLViewRatification || canManagerViewRatification || canHRViewRatification;
              // Form is editable only if it's an SL in SL_REVIEW status. Otherwise, it's read-only.
              const ratificationFormReadOnly = !canSLEditRatification;
              
              // For COMPLETED, use canViewCompletedDetails and always read-only
              if (packet.status === PacketStatus.COMPLETED && canViewCompletedDetails) {
                return (
                  <div className="mb-10">
                    <RatificationForm
                      initialRatification={packet.ratification}
                      initialOverruleJustification={packet.overruleJustification || undefined}
                      onSubmit={handleSaveRatification}
                      isSubmitting={saveRatificationMutation.isPending}
                      readOnly={true}
                    />
                  </div>
                );
              }
              // For all other statuses, preserve the old logic
              if (shouldRenderRatificationForm) {
                return (
                  <div className="mb-10">
                    <RatificationForm
                      initialRatification={packet.ratification}
                      initialOverruleJustification={packet.overruleJustification || undefined}
                      onSubmit={handleSaveRatification}
                      isSubmitting={saveRatificationMutation.isPending}
                      readOnly={ratificationFormReadOnly}
                    />
                  </div>
                );
              }
              return null;
            })()}
            
            {/* Tabs for Criteria, Endorsements, and Assessments */}
            <Tabs
              value={activeTab}
              onValueChange={handleTabChange}
              className="mb-16"
            >
              {/* Mobile: dropdown menu selector */}
              <div className="sm:hidden mb-10 pb-4">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button ref={triggerRef} variant="outline" className="w-full justify-between border-gray-300">
                      {tabOptions.find(o => o.value === activeTab)?.label}
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    sideOffset={0}
                    align="start"
                    className="bg-background"
                    style={{ minWidth: triggerWidth, width: 'max-content' }}
                  >
                    {tabOptions.map(opt => (
                      <DropdownMenuItem
                        key={opt.value}
                        onSelect={() => handleTabChange(opt.value)}
                        className={`w-full ${opt.value === activeTab ? 'font-semibold' : ''}`}
                      >
                        {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {/* Desktop: horizontal tab list */}
              <TabsList className="hidden sm:inline-flex flex-row overflow-x-auto items-center justify-center space-x-1 mb-10">
                <TabsTrigger className="flex-shrink-0 px-4" value="criteria">Criteria</TabsTrigger>
                <TabsTrigger className="flex-shrink-0 px-4" value="endorsements">Endorsements</TabsTrigger>
                {showOpportunitiesTab && (
                  <TabsTrigger className="flex-shrink-0 px-4" value="opportunities">Opportunities</TabsTrigger>
                )}
                {showAssessmentTabs && (
                  <>
                    <TabsTrigger className="flex-shrink-0 px-4" value="blinded">Blinded Assessments</TabsTrigger>
                    <TabsTrigger className="flex-shrink-0 px-4" value="unblinded">Unblinded Assessments</TabsTrigger>
                  </>
                )}
                {showBarRaiserTab && (
                  <TabsTrigger className="flex-shrink-0 px-4" value="bar-raiser">Bar Raiser Review</TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="criteria">
                <CriteriaSection
                  packetId={packet.id}
                  isEditor={isEditor}
                  isCandidate={isCandidate}
                  readOnly={supportingContentReadOnly}
                />
              </TabsContent>

              <TabsContent value="endorsements">
                <EndorsementsSection
                  packetId={packet.id}
                  readOnly={supportingContentReadOnly}
                  isEditor={isEditor}
                  isCandidate={isCandidate}
                />
              </TabsContent>

              {showOpportunitiesTab && (
                <TabsContent value="opportunities">
                  <ConcernsSection
                    packetId={packet.id}
                    readOnly={supportingContentReadOnly}
                    isEditor={isEditor}
                    isSeniorLeader={isPacketSeniorLeader}
                  />
                </TabsContent>
              )}

              {showAssessmentTabs && (
                <>
                  <TabsContent value="blinded">
                    <BlindAssessmentsSection
                      packetId={packet.id}
                      readOnly={packet.status !== PacketStatus.BLINDED_ASSESSMENT}
                      isEditor={isEditor}
                      canViewAssessments={showAssessmentTabs}
                    />
                  </TabsContent>
                  <TabsContent value="unblinded">
                    <UnblindedAssessmentsSection
                      packetId={packet.id}
                      readOnly={packet.status !== PacketStatus.UNBLINDED_ASSESSMENT}
                      isEditor={isEditor}
                      canViewAssessments={showAssessmentTabs}
                    />
                  </TabsContent>
                </>
              )}

              {showBarRaiserTab && (
                <TabsContent value="bar-raiser">
                  <BarRaiserReviewSection
                    packetId={packet.id}
                    readOnly={packet.status === PacketStatus.COMPLETED}
                    isManagerOrSL={isEditor || isSeniorLeader || isGlobalSeniorLeader}
                    isBarRaiser={isBarRaiser}
                    canViewAssessments={showBarRaiserTab}
                  />
                </TabsContent>
              )}
            </Tabs>
          </div>
        )}
      </FormProvider>
    </>
  );
}

// export default PacketPage; // Keep default export commented if not needed by router setup
